import {delay, json, texture} from "p3";
import { Rectangle } from "pixi.js-legacy";
import common from "../common";
import definitions from "../definitions";
import BossFactory from "../game/boss/BossFactory";
import BossBert from "../game/boss/player/BossBert";
import BossIvandoe from "../game/boss/player/BossIvandoe";
import HUD from "../ui/boss/HUD";
import IdleMessage from "../ui/idlemessage/IdleMessage";
import { convertAr, iswebfont, locale, resizeLabelToFitText } from "../utils";
import Scene from "./Scene";

export default class BossFightScene extends Scene {
    /**
     * @param {string} bossId
     */
    constructor(bossId) {
        super("boss_" + bossId);

        this._betweenTurnsDelay = 1;
        this._bossAttackDelay = 0.25;

        this._bossId = bossId;
        this._currentDamageDelt = 0;

        this._defaultTween = null;
        this._cameraIdleTween = null;
        this._floatingTextTween = null;

        this._boss = null;
        this._ivandoe = null;
        this._bert = null;

        this._background = null;
        this._hud = null;
        this._camera = null;
        this._cameraTarget = null;
        this._sceneLayer = null;
        this._foregroundLayer = null;
        this._actionWheelHitBox = null;
        this._actionWheelHitBoxContainer = null;
        this._bgm = null;

        this._adPerk = null;
        this._adPerkText = null;
        this._attackMultiplier = 1;
        this._damageMultiplier = 1;

        this._endTransitionDelay = 1;

        this._reviveAdAccepted = false;
    }

    init() {
        super.init();
    }

    onSceneLoaded() {
        super.onSceneLoaded();

        if (!common.saveData.settings.musicMute) {
            this._bgm = P3.audio.playSound([
                "music_boss_fight_loop_00", 
                "music_boss_fight_loop_01", 
                "music_boss_fight_loop_02",
                "music_boss_fight_loop_03"
            ], {loop:true});
        }
        this._sceneLayer = new PIXI.Container();
        this.addChild(this._sceneLayer);

        this._foregroundLayer = new PIXI.Container();
   
        this._createBackground();

        this._actionWheelHitBoxContainer = new PIXI.Container();
        this._actionWheelHitBoxContainer.interactive = true;
        this._actionWheelHitBoxContainer.hitArea = new Rectangle(0, 0, common.STAGE_WIDTH, P3.View.height);
        this.addChild(this._actionWheelHitBoxContainer);

        this._createBoss();
        this._createPlayer();

        this.addChild(this._foregroundLayer);

        this._createUI();

        this._camera = new P3.Camera(new PIXI.Point(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.27), new PIXI.Point(-common.STAGE_WIDTH * 0.25, common.STAGE_HEIGHT * 0.2));
        this._camera.addLayer(new P3.CameraLayer(this._sceneLayer, new PIXI.Point(1, 1)));
        this._camera.addLayer(new P3.CameraLayer(this._foregroundLayer, new PIXI.Point(1, 1)));
        this._camera.zoom = 1;
        this._cameraTarget = new PIXI.Point(0, 0);
        this._camera.setTarget(this._cameraTarget);

        if(common.ads.isAdEnabled(definitions.AD_PLACEMENTS.BOSS_BATTLE_PERK)) {
            this._offerAd(definitions.AD_PLACEMENTS.BOSS_BATTLE_PERK, (result, perk) => {
                if(result) {   
                    let text;
                    let textColour;
                    if(perk == definitions.AD_PLACEMENTS.BOSS_BATTLE_PERK_TYPES.MORE_POWER) {
                        this._attackMultiplier = 2;
                        text = locale("ad_battle_perk_highlight_attack");
                        textColour = 0x4bb6b5;
                    } else if(perk == definitions.AD_PLACEMENTS.BOSS_BATTLE_PERK_TYPES.BETTER_DEFENCE) {
                        this._damageMultiplier = 0.5;
                        text = locale("ad_battle_perk_highlight_defence");
                        textColour = 0xd15c84;
                    } else if(perk == definitions.AD_PLACEMENTS.BOSS_BATTLE_PERK_TYPES.REDUCED_BOSS_ACCURACY) {
                        this._boss.hitChance *= 0.5;
                        this._boss.missChance *= 2;
                        text = locale("ad_battle_perk_highlight_miss_chance");
                        textColour = 0xfcf651
                    }
                    if (perk != null) {
                        this._adPerk = perk;
                        if (!iswebfont()) {
                            this._adPerkText = new PIXI.BitmapText(text, {
                                fontName: "Alghera Pro Outline",
                                fontSize: 35,
                                align: "center",
                                tint: textColour,
                                maxWidth: 150
                            });
                        } else {
                            this._adPerkText = new PIXI.Text(text, {
                                fontName: "FredFredBurger",
                                fontSize: 35,
                                align: "center",
                                tint: textColour,
                                wordWrap: true,
                                wordWrapWidth: 150
                            });
                        }
                        this._adPerkText.alpha = 1;
                        this._adPerkText.anchor.set(0.5);
                        this._adPerkText.visible = false;
                        this._tweens.push(
                            gsap.to(this._adPerkText.scale, 0.4, {x:1.2, y:1.2, yoyo:true, repeat:-1, ease:Sine.easeInOut})
                        );
                    }
                }
                this._onBegin();
            })
        } else {
            this._onBegin();
        }
    }

    update() {
        super.update();

        this._boss && this._boss.update();
        this._ivandoe && this._ivandoe.update();
        this._bert && this._bert.update();
        this._hud && this._hud.update();
        this._camera && this._camera.update();
    }

    resize() {
        super.resize();

        this._hud && this._hud.resize();

        if (this._actionWheelHitBoxContainer) {
            this._actionWheelHitBoxContainer.hitArea = new Rectangle(0, 0, common.STAGE_WIDTH, P3.View.height);
        }
    }

    _createBackground() {
        switch (this._bossId) {
            case definitions.BOSS_ID.GIANT_SPIDER: {
                    const bg = new PIXI.Sprite(texture("spider_cave_bg_1x"));
                    bg.width = common.STAGE_WIDTH;
                    bg.height = 1000;
                    bg.position.set(-common.STAGE_WIDTH / 2, -common.STAGE_HEIGHT / 2);
                    this._sceneLayer.addChild(bg);
                }
                break;
            case definitions.BOSS_ID.CHICK_OF_DOOM: {
                    const bg = new PIXI.Sprite(texture("chicken_farm_bg_1x"));
                    bg.width = common.STAGE_WIDTH;
                    bg.height = 1000;
                    bg.position.set(-common.STAGE_WIDTH * 0.5, -P3.View.height * 0.5);
                    this._sceneLayer.addChild(bg);

                    const foregroundBg = new PIXI.Sprite(texture("boss_foreground_chick_farm"));
                    foregroundBg.position.set(-common.STAGE_WIDTH * 0.5, 100);
                    this._sceneLayer.addChild(foregroundBg);
                }
                break;
            case definitions.BOSS_ID.BATWITCH: {
                    const bg = new PIXI.Sprite(texture("batwitch_forest_bg_1x"));
                    bg.width = common.STAGE_WIDTH;
                    bg.height = 1000;
                    bg.position.set(-common.STAGE_WIDTH * 0.5, -P3.View.height * 0.5);
                    this._sceneLayer.addChild(bg);

                    const foregroundBg = new PIXI.Sprite(texture("boss_foreground_batwitch_forest"));
                    foregroundBg.position.set(-common.STAGE_WIDTH * 0.5, 300);
                    this._foregroundLayer.addChild(foregroundBg);
                }
                break;
            case definitions.BOSS_ID.SUZIE: {
                    const bg = new PIXI.Sprite(texture("suzie_swamp_bg_1x"));
                    bg.width = common.STAGE_WIDTH;
                    bg.height = 1000;
                    bg.position.set(-common.STAGE_WIDTH / 2, -common.STAGE_HEIGHT / 2);
                    this._sceneLayer.addChild(bg);
                    
                    const foregroundBg = new PIXI.Sprite(texture("boss_foreground_susie_swamp"));
                    foregroundBg.position.set(-common.STAGE_WIDTH * 0.5, 100);
                    this._foregroundLayer.addChild(foregroundBg);
                }
                break;
            case definitions.BOSS_ID.STEFAN: {
                    const bg = new PIXI.Sprite(texture("stefan_bg_1x"));
                    bg.width = common.STAGE_WIDTH;
                    bg.height = 1000;
                    bg.position.set(-common.STAGE_WIDTH * 0.5, -P3.View.height * 0.5);
                    this._sceneLayer.addChild(bg);
                }
                break;
            case definitions.BOSS_ID.CENTICORE: {
                    const bg = new PIXI.Sprite(texture("centicore_bg_1x"));
                    bg.width = common.STAGE_WIDTH;
                    bg.height = 1000;
                    bg.position.set(-common.STAGE_WIDTH * 0.5, -P3.View.height * 0.5);
                    this._sceneLayer.addChild(bg);
                }
                break;
            case definitions.BOSS_ID.GNOME_BEAR: {
                    const bg = new PIXI.Sprite(texture("gnome_bear_bg_1x"));
                    bg.width = common.STAGE_WIDTH;
                    bg.height = 1000;
                    bg.position.set(-common.STAGE_WIDTH * 0.5, -P3.View.height * 0.5);
                    this._sceneLayer.addChild(bg);
                }
                break;
            case definitions.BOSS_ID.CYCLOPS: {
                    const bg = new PIXI.Sprite(texture("one_eyed_troll_bg_1x"));
                    bg.width = common.STAGE_WIDTH;
                    bg.height = 1000;
                    bg.position.set(-common.STAGE_WIDTH * 0.5, -P3.View.height * 0.5);
                    this._sceneLayer.addChild(bg);
                }
                break;
            case definitions.BOSS_ID.BUFF_SOW: {
                    const bg = new PIXI.Sprite(texture("buff_sow_bg_1x"));
                    bg.width = common.STAGE_WIDTH;
                    bg.height = 1000;
                    bg.position.set(-common.STAGE_WIDTH * 0.5, -P3.View.height * 0.5);
                    this._sceneLayer.addChild(bg);
                                        
                    const foregroundBg = new PIXI.Sprite(texture("boss_foreground_buff_sow_castle"));
                    foregroundBg.position.set(-common.STAGE_WIDTH * 0.5, 380);
                    this._foregroundLayer.addChild(foregroundBg);
                }
                break;
            case definitions.BOSS_ID.FISH_MONSTER: {
                    const bg = new PIXI.Sprite(texture("fish_monster_bg_1x"));
                    bg.width = common.STAGE_WIDTH;
                    bg.height = 1000;
                    bg.position.set(-common.STAGE_WIDTH * 0.5, -P3.View.height * 0.5);
                    this._sceneLayer.addChild(bg);
                }
                break;
            case definitions.BOSS_ID.QUEEN_MOLEWORM: {
                    const bg = new PIXI.Sprite(texture("queen_worm_bg_1x"));
                    bg.width = common.STAGE_WIDTH;
                    bg.height = 1000;
                    bg.position.set(-common.STAGE_WIDTH * 0.5, -P3.View.height * 0.5);
                    this._sceneLayer.addChild(bg);   

                    const foregroundBg = new PIXI.Sprite(texture("boss_foreground_queen_worm"));
                    foregroundBg.position.set(-common.STAGE_WIDTH * 0.5, 0);
                    this._foregroundLayer.addChild(foregroundBg);
                }
                break;
        }
    }

    _shakeCamera() {}

    _createBoss() {
        this._boss = BossFactory.create(this._bossId, {});
        this._boss.init();

        if (this._boss) {
            this._boss.position.set(common.STAGE_WIDTH * 0.7, common.STAGE_HEIGHT * 0.6);
            this._boss.scale.set(-1, 1);
            this._boss.signals.attackEnd.add(() => delay(this._betweenTurnsDelay, () => this._onStartPlayerAttack()), this);
            this._boss.signals.dead.add(this._onComplete, this);
            this._boss.signals.bossStrike.add(this._onEnemyAttack, this);
        }

        this._sceneLayer.addChild(this._boss);
    }

    _createPlayer() {
        this._ivandoe = new BossIvandoe();
        this._ivandoe.init();

        this._bert = new BossBert();
        this._bert.init();

        if (this._ivandoe) {
            this._ivandoe.position.set(-common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.45);
            this._ivandoe.signals.dead.add(this._onFail, this);
            this._ivandoe.signals.damageDelt.add(() => {
                this._hud.bossHealthBar.health = Math.max(0, this._hud.bossHealthBar.health - this._currentDamageDelt);

                this._shake(-70);
    
                if (this._currentDamageDelt > 0) {
                    this._spawnFloatingText(locale('ui_boss_hit'), 0, true);
    
                    P3.audio.playSound("sfx_ivandoe_hit_enemy");
    
                    this._boss.takeDamage(this._currentDamageDelt);
                } else {
                    this._spawnFloatingText(locale('ui_boss_miss'), 0, true);
                }
            }, this);
        }

        if (this._bert) this._bert.position.set(-common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.2);

        this._sceneLayer.addChild(this._ivandoe);
        this._sceneLayer.addChild(this._bert);
    }

    _createUI() {
        this._hud = new HUD(this._ivandoe, this._boss);
        this._hud.signals.attack.add((damage) => this._onPlayerAttack(damage), this);
        this._hud.signals.heal.add(() => this._onPlayerHeal(), this);
        this._hud.signals.miss.add(() => this._onPlayerAttack(0), this);
        this._hud.pauseButton.signals.click.add(this.onPauseButtonClick, this);
        this._hud.surrenderButton.signals.click.add(this.onSurrenderButtonClick, this);
        this.addChild(this._hud);
        
        this._actionWheelHitBoxContainer.on("pointerdown", () => this._hud.attackBar.onMouseClick());
    }

    onSurrenderButtonClick(button) {
        this._hud.attackBar.animateOut().then(() => {
            this._defaultTween = gsap.timeline();
            this.addTween(this._defaultTween);
    
            this._ivandoe.playGiveInAnimation();
            
            this._defaultTween.add(gsap.to(this._ivandoe.position, 5, {
                x: -common.STAGE_WIDTH * 0.5,
                y: this._ivandoe.y,
                ease: Power0.easeInOut,
                onStart: () => {
                    this._ivandoe.facingDirection = -1;
                }
            }), 0);
    
            this._defaultTween.add(gsap.to(this._bert.position, 5, {
                x: -common.STAGE_WIDTH * 0.5,
                y: this._bert.y,
                ease: Power0.easeInOut,
                onStart: () => {
                    this._bert.facingDirection = -1;
                }
            }), 0);
    
            this.delay(1).then(() => this.signals.next.dispatch(false));
        });
    }

    /**
     * @param {P3.Button} button 
     */
    onPauseButtonClick(button) {
        this.signals.pause.dispatch();
    }

    _onStartPlayerAttack() {
        if(this._ivandoe.isDead) {
            return;
        }

        this._zoomOutFromFocus().then(() => 
        {
            this._camera.focus.set(-common.STAGE_WIDTH * 0.25, common.STAGE_HEIGHT * 0.2);

            this._zoomInToFocus().then(() => {
                this._hud.attackBar.animateIn().then(() => {
                    this._hud.attackBar.enabled = true;
                });
            });
        });
    }

    _onPlayerHeal() { 
        this._hud.attackBar.animateOut();

        this._defaultTween = gsap.timeline();
        this.addTween(this._defaultTween);

        this._defaultTween.add(gsap.to(this._bert.position, 1.5, {
            x: this._ivandoe.position.x - 50,
            y: this._ivandoe.position.y - 50,
            ease: Power1.easeInOut,
            yoyo: true,
            repeat: 1,
            onStart: () => {
                P3.audio.playSound("vo_bert_heal_00");
                this._bert.signals.healIvandoe.dispatch();
            },
            onRepeat: () => {
                this._bert.playIdleAnimation(false);
                this._ivandoe.playHealAnimation();

                this._spawnFloatingText(locale('ui_boss_healed'), -common.STAGE_WIDTH * 0.15, true);

                if (this._ivandoe.health < this._ivandoe.maxHealth) {
                    this._ivandoe.health = this._ivandoe.health + 6;

                    if (this._ivandoe.health >= this._ivandoe.maxHealth) {
                        this._ivandoe.health = this._ivandoe.maxHealth;
                    }

                    this._hud.playerHealthBar.health = Math.max(0, this._hud.playerHealthBar.health + 6);        
                }
            },
            onComplete: () => this._onStartEnemyAttack()
        }), 0);
    }

    /**
     * @param {number} damage 
     */
    _onPlayerAttack(damage) {
        this._currentDamageDelt = damage * this._attackMultiplier;
        console.log('PLAYER DAMAGE:', damage, this._currentDamageDelt);

        this._hud.attackBar.animateOut().then(() => {
            this._ivandoe.signals.attack.dispatch();

            delay(this._betweenTurnsDelay, () => this._onStartEnemyAttack());
        });
    }

    /**
     * @param {number} offset 
     */
    _shake(offset) {
        this._defaultTween = gsap.timeline();
        this.addTween(this._defaultTween);

        this._defaultTween.add(gsap.to(this._cameraTarget, 0.1, {
            x: this._cameraTarget.x + offset,
            y: this._cameraTarget.y,
            yoyo: true,
            repeat: 1,
            ease: Circ.easeInOut,
            onComplete: () => {}
        }), 0);
    }

    _zoomInToFocus() {
        return new Promise((resolve) => {
            this._defaultTween = gsap.timeline();
            this.addTween(this._defaultTween);
    
            this._defaultTween.add(gsap.to(this._camera, 0.35, {
                zoom: 1.35,
                ease: Power1.easeInOut,
                onComplete: () => {
                    resolve();
                }
            }), 0);
        });
    }

    _zoomOutFromFocus() {
        this.removeTween(this._cameraIdleTween);

        return new Promise((resolve) => {
            this._defaultTween = gsap.timeline();
            this.addTween(this._defaultTween);
    
            this._defaultTween.add(gsap.to(this._camera, 0.35, {
                zoom: 1.0,
                ease: Power1.easeInOut,
                onComplete: () => {
                    resolve();
                }
            }), 0);
        });
    }

    _onEnemyAttack() {
        const damage = Math.round(this._boss.getDamageAmount() * this._damageMultiplier);
        console.log('ENEMY DAMAGE:', damage, this._currentDamageDelt);
        this._shake(70);
        
        if (damage > 0) {
            this._spawnFloatingText(locale('ui_boss_hit'), -common.STAGE_WIDTH * 0.15, false);
            
            this._ivandoe.signals.takeDamage.dispatch(damage);

            this._hud.playerHealthBar.health = Math.max(0, this._hud.playerHealthBar.health - damage);
        } else {
            this._spawnFloatingText(locale('ui_boss_miss'), -common.STAGE_WIDTH * 0.15, false);
        }
    }

    _onStartEnemyAttack() {
        if (this._boss.isDead) {
            return;
        }

        this._zoomOutFromFocus().then(() => {
            this._camera.focus.set(-(common.STAGE_WIDTH * 0.05), common.STAGE_HEIGHT * 0.2);
    
            this._hud.attackBar.animateOut().then(() => {
                this._zoomInToFocus().then(() => {      
                    this.delay(this._bossAttackDelay).then(() => {
                        this._boss.state = definitions.BOSS_STATE.ATTACK;
                    });
                });
            });
        });
    }

    /**
     * @param {string} text 
     * @param {number} x 
     * @param {number} isIvandoe 
     */
    _spawnFloatingText(text, x, isIvandoe) {
        let action;
        if (!iswebfont()) {
            action = new PIXI.BitmapText(text, {
                fontName: "Alghera Pro Outline",
                fontSize: 50,
                align: "center",
                tint: 0xFFFFFF
            });
        } else {
            action = new PIXI.Text(text, {
                fontName: "FredFredBurger",
                fontSize: 50,
                align: "center",
                tint: 0xFFFFFF
            });
        }

        action.alpha = 1;
        action.position.set(x, 100);
        action.anchor.set(0.5);
        action.scale.set(0);
        this._sceneLayer.addChild(action);

        this._floatingTextTween = gsap.timeline();
        this.addTween(this._floatingTextTween);

        this._floatingTextTween.add(gsap.to(action.position, 2, {
            x: x,
            y: -common.STAGE_HEIGHT * 0.1,
            ease: Cubic.easeOut,
            onComplete: () => {}
        }), 0);

        this._floatingTextTween.add(gsap.to(action, 2, {
            alpha: 0,
            ease: Cubic.easeOut,
            onComplete: () => {
                this._sceneLayer.removeChild(action);
                if (this._adPerkText) {
                    this._adPerkText.visible = false;
                }
            }
        }), 0);

        this._floatingTextTween.add(gsap.to(action.scale, 0.24, {
            x: 1,
            y: 1,
            ease: "back.out(1)",
            onComplete: () => {}
        }), 0);

        let showAdPerkText = false;
        
        if (this._adPerk == definitions.AD_PLACEMENTS.BOSS_BATTLE_PERK_TYPES.MORE_POWER && 
            text == locale('ui_boss_hit') && isIvandoe) {
            showAdPerkText = true;
        } else if (this._adPerk == definitions.AD_PLACEMENTS.BOSS_BATTLE_PERK_TYPES.BETTER_DEFENCE &&
            text == locale('ui_boss_hit') && !isIvandoe) {
            showAdPerkText = true;
        } else if (this._adPerk == definitions.AD_PLACEMENTS.BOSS_BATTLE_PERK_TYPES.REDUCED_BOSS_ACCURACY &&
            text == locale('ui_boss_miss') && !isIvandoe) {
            showAdPerkText = true;
        }
        console.log(this._adPerk, text, isIvandoe, showAdPerkText);
        if (showAdPerkText) {
            this._sceneLayer.addChild(this._adPerkText);
            this._adPerkText.alpha = 1;
            this._adPerkText.position.set(action.x, action.y);
            this._adPerkText.visible = true;
            this._tweens.push(gsap.to(this._adPerkText, 1.5, {y:action.y - 50, ease:Sine.easeOut}));
            this._tweens.push(gsap.to(this._adPerkText, 0.5, {delay:1, alpha:0, ease:Sine.easeOut}));
        }
    }

    _offerAd(type, callback) {

        const panel = new PIXI.NineSlicePlane(texture("panel_popup_9slice"), 80, 110, 80, 80);
        panel.width = 520;
        panel.height = 310;
        panel.x = (common.STAGE_WIDTH/2) - (panel.width/2);
        panel.y = common.STAGE_HEIGHT;
        this.addChild(panel);

        const viewAdLabelHolder = new PIXI.NineSlicePlane(texture('ad_label'), 17, 7, 17, 7);
        panel.addChild(viewAdLabelHolder);

        let viewAdLabel;
        if (!iswebfont()) {
            viewAdLabel = new PIXI.BitmapText(locale('ui_watch_ad_description'), {
                fontName: "Alghera Pro",
                fontSize: 24,
                align: "left",
                tint: 0x000000
            });
        } else {
            viewAdLabel = new PIXI.Text(locale('ui_watch_ad_description'), {
                fontFamily: "FredFredBurger",
                fontSize: 22,
                align: "left",
                fill: 0x0
            });
        }
        viewAdLabel.anchor.set(0.5);
        viewAdLabelHolder.addChild(viewAdLabel);
        resizeLabelToFitText(viewAdLabel, (panel.width - 50) - viewAdLabelHolder.leftWidth - viewAdLabelHolder.rightWidth);

        viewAdLabelHolder.width = viewAdLabel.width + viewAdLabelHolder.leftWidth + viewAdLabelHolder.rightWidth;
        viewAdLabelHolder.position.set((panel.width/2) - (viewAdLabelHolder.width/2), -(viewAdLabelHolder.height) + 10);
        viewAdLabel.position.set(viewAdLabelHolder.width / 2, (viewAdLabelHolder.height/2) + 2);

        let title;
        let imageTex;
        const text = [];
        let perk = [
            definitions.AD_PLACEMENTS.BOSS_BATTLE_PERK_TYPES.MORE_POWER,
            definitions.AD_PLACEMENTS.BOSS_BATTLE_PERK_TYPES.BETTER_DEFENCE,
            definitions.AD_PLACEMENTS.BOSS_BATTLE_PERK_TYPES.REDUCED_BOSS_ACCURACY,
        ].splice(Math.floor(Math.random() * 3), 1)[0];

        if(type == definitions.AD_PLACEMENTS.BOSS_BATTLE_PERK) {
            text.push(
                'ad_battle_perk',
                'ad_battle_perk_description',
                'ad_battle_perk_' + perk.toLowerCase()
            )
            imageTex = 'prop_ui_battle_perk_00';
        } else if (type == definitions.AD_PLACEMENTS.BOSS_RESUME) {
            text.push(
                'ad_battle_resume',
                'ad_battle_resume_description'
            )
            imageTex = 'prop_ui_life_recharge_00';
            perk = null;
        }
        if (!iswebfont()) {
            title = new PIXI.BitmapText(locale(text[0]).toUpperCase(), {
                fontName: "Alghera Pro",
                fontSize: 40,
                align: "left",
                tint: 0x000000
            });
        } else {
            title = new PIXI.Text(locale(text[0]).toUpperCase(), {
                fontFamily: "FredFredBurger",
                fontSize: 40,
                align: "left",
                fill: 0x0
            });
        }
        title.anchor.set(0, 0.5);
        title.position.set(40, !iswebfont() ? 60 : 57);
        panel.addChild(title);
        resizeLabelToFitText(title, panel.width-80);

        const image = new PIXI.Sprite(texture(imageTex));
        image.anchor.set(0.5, 0.5);
        image.position.set(panel.width - 40 - (image.width/2), 80 + (image.height/2));
        panel.addChild(image);

        let descLabel;
        if (!iswebfont()) {
            descLabel = new PIXI.BitmapText(locale(text[1]), {
                fontName: "Alghera Pro",
                fontSize: 32,
                align: "left",
                tint: 0x000000,
                maxWidth: panel.width - 80 - (image.width)
            });
        } else {
            descLabel = new PIXI.Text(locale(text[1]), {
                fontFamily: "FredFredBurger",
                fontSize: 32,
                align: "center",
                fill: 0x0,
                wordWrap: true,
                wordWrapWidth: panel.width - 80 - (image.width)
            });
        }
        descLabel.position.set(40, 120);
        panel.addChild(descLabel);

        let bottomLimit = Math.max(descLabel.y + descLabel.height, image.y + (image.height/2));

        if(text[2]) {
            let perkText;
            if (!iswebfont()) {
                perkText = new PIXI.BitmapText(locale(text[2]).toUpperCase(), {
                    fontName: "Alghera Pro",
                    fontSize: 30,
                    align: "center",
                    tint: 0x702311,
                    maxWidth: panel.width-80
                });
            } else {
                perkText = new PIXI.Text(locale(text[2]).toUpperCase(), {
                    fontFamily: "FredFredBurger",
                    fontSize: 30,
                    align: "center",
                    fill: 0x702311,
                    wordWrap: true,
                    wordWrapWidth: panel.width-80
                });
            }
            perkText.y = !iswebfont() ? 0 : -3;
            perkText.anchor.set(0.5, 0);
            perkText.position.set(panel.width/2, bottomLimit + 20);
            panel.addChild(perkText);

            bottomLimit = perkText.y + perkText.height + 20;
        }

        panel.height = Math.max(panel.height, bottomLimit + 50);

        let errorMessage;

        const hidePanel = () => {
            this._tweens.push(
                gsap.to(panel, 0.3, {y:common.STAGE_HEIGHT, ease:Expo.easeIn, onComplete:() => {
                    this.removeChild(panel);
                }})
            );
            if (errorMessage) {
                try{
                    this.removeChild(errorMessage);
                } catch(e){}
                errorMessage.destroy();
                errorMessage = null;
            }
        }

        const watchButton = P3.Button.create(texture("btn_rectangle_out"));
        watchButton.overSoundName = "sfx_btn_generic_rollover";
        watchButton.clickSoundName = "sfx_btn_generic_press_00";

        let nameLabel;
        if (!iswebfont()) {
            nameLabel = new PIXI.BitmapText(locale('ui_watch_ad').toUpperCase(), {
                fontName: "Alghera Pro Outline",
                fontSize: 32,
                align: "left",
                tint: 0xFFFFFF
            });
        } else {
            nameLabel = new PIXI.Text(locale('ui_watch_ad').toUpperCase(), {
                fontFamily: "FredFredBurger",
                fontSize: 32,
                align: "left",
                fill: 0xFFFFFF
            });
        }
        nameLabel.y = !iswebfont() ? 0 : -3;
        nameLabel.anchor.set(0.5);
        watchButton._background.addChild(nameLabel);
        resizeLabelToFitText(nameLabel, 280);

        watchButton.x = (panel.width/2);
        watchButton.y = panel.height;
        panel.addChild(watchButton);

        const closeButton = P3.Button.create(
            texture("btn_small_out"),
            texture("btn_small_out"),
            texture("btn_small_out"),
            texture("btn_icon_cross"),
        );
        closeButton.x = panel.width + 20;
        closeButton.y = 20;
        panel.addChild(closeButton);

        this.signalListeners.push(
            watchButton.signals.click.add(() => {

                common.ads.signals.viewed.detachAll();
                common.ads.signals.fail.detachAll();
                
                watchButton.interactive = closeButton.interactive = false;
                watchButton.alpha = closeButton.alpha = 0.5;

                const getReward = () => {
                    callback(true, perk);
                    hidePanel();
                }
                if (common.ads.showAd()) {
                    this.signalListeners.push(
                        common.ads.signals.viewed.once((rewarded) => {
                            if (rewarded) {
                                getReward();
                            } else {
                                watchButton.interactive = closeButton.interactive = true;
                                watchButton.alpha = closeButton.alpha = 1;
                            }
                        }),
                        common.ads.signals.fail.once(() => {
                            watchButton.interactive = closeButton.interactive = true;
                            watchButton.alpha = closeButton.alpha = 1;
                            
                            if(errorMessage == null) {
                                errorMessage = new IdleMessage(locale("ad_fail_message"), () => {
                                    try{
                                        this.removeChild(errorMessage);
                                    } catch(e){}
                                    errorMessage.destroy();
                                    errorMessage = null;
                                });
                                errorMessage.x = (common.STAGE_WIDTH / 2) - (P3.View.width / 2);
                                errorMessage.y = 150;
                                this.addChild(errorMessage);
                            }
                        })
                    );
                } else {
                    getReward();
                }
            }, this)
        )
        
        this.signalListeners.push(
            closeButton.signals.click.add(() => {
                callback(false);
                hidePanel();
            }, this)
        );
        this._tweens.push(
            gsap.to(panel, 0.3, {y:(common.STAGE_HEIGHT / 2) - (panel.height/2), ease:Expo.easeOut})
        );
    }

    _onBegin() {
        this._defaultTween = gsap.timeline();
        this.addTween(this._defaultTween);

        this._defaultTween.add(gsap.to(this._boss.position, 1, {
            x: common.STAGE_WIDTH * 0.08,
            y: common.STAGE_HEIGHT * 0.4,
            ease: Back.easeInOut,
            delay: 0.5,
            onComplete: () => {}
        }), 0);

        this._defaultTween.add(gsap.to(this._ivandoe.position, 1.25, {
            x: -common.STAGE_WIDTH * 0.19,
            y: common.STAGE_HEIGHT * 0.45,
            ease: Power1.easeInOut,
            delay: 0.5,
            onStart: () => this._ivandoe.signals.enter.dispatch(),
            onComplete: () => {
                this._hud.attackBar.animateIn().then(() => {
                    this._hud.attackBar.enabled = true;
                    this._hud.surrenderButton.enabled = true;
                });
                this._zoomInToFocus().then();
                this._ivandoe.playIdleAnimation();
            }
        }), 0);

        this._defaultTween.add(gsap.to(this._bert.position, 1, {
            x: -common.STAGE_WIDTH * 0.27,
            y: common.STAGE_HEIGHT * 0.25,
            ease: Back.easeInOut,
            delay: 0.5,
            onComplete: () => {}
        }), 0);
    }

    _onComplete() {
        this._bgm && this._bgm.stop();
        
        P3.audio.playSound("vo_iv_accept_quest");
        if (!common.saveData.settings.musicMute) {
            P3.audio.playSound("music_boss_defeated");
        }

        this._boss.state = definitions.BOSS_STATE.DEAD;
        this._hud.attackBar.animateOut().then(() => {
            this._ivandoe.playWinAnimation();

            this.delay(0.3).then(() => {
                this._hud.animateInWinScreen().then(() => {
                    this.delay(this._endTransitionDelay).then(() => this.signals.next.dispatch(true));
                });
            })
        })

        common.tracking.trackEvent(definitions.TRACKING.EVENT_TYPE.INTERACTION, {
            section: common.tracking.getSection(),
            contenttitle: locale(json(this._bossId).character.name),
            interaction: "battle boss win",
            progress: common.tracking.progress,
            interactionevent: 1
        });
    }

    _onFail() {
        const die = () => {
            this._hud.attackBar.animateOut().then(() => {
                this._ivandoe.playDeathAnimation();

                this.delay(0.3).then(() => {
                    this._hud.animateInLoseScreen().then(() => {
                        this.delay(this._endTransitionDelay).then(() => {
                            this.signals.next.dispatch(false);
                        });
                    });
                });

                common.tracking.trackEvent(definitions.TRACKING.EVENT_TYPE.INTERACTION, {
                    section: common.tracking.getSection(),
                    contenttitle: locale(json(this._bossId).character.name),
                    interaction: "battle boss lose",
                    progress: common.tracking.progress,
                    interactionevent: 1
                });
            });
        }
        if(common.ads.isAdEnabled(definitions.AD_PLACEMENTS.BOSS_RESUME) && !this._reviveAdAccepted) {
            this._offerAd(definitions.AD_PLACEMENTS.BOSS_RESUME, (result) => {
                if (result) {
                    this._hud.playerHealthBar.setHearts(this._ivandoe.maxHealth / 4, true);
                    this._ivandoe.health = this._ivandoe.maxHealth;
                    this._onStartPlayerAttack();
                    this._reviveAdAccepted = true;
                } else {
                    die();
                }
            })
        } else {
            die();
        }
    }

    /***/
    get bgm() {
        return this._bgm;
    }
}